import { DeleteOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Checkbox, Col, Form, Input, message, Popconfirm, Row, Space, Spin } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLoginModal } from '../../../../context/loginModalContext'

import { useUser } from '../../../../context/userContext'
import { showAuthError } from '../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../common/CommonStyles'
import EmailField from '../../../common/formFields/EmailField'
import NameField from '../../../common/formFields/NameField'
import PhoneField from '../../../common/formFields/PhoneField'
import { ADD_NEW_LAB, DELETE_LAB, EDIT_LAB } from '@dentolize/clinic-mobile/src/shared/store/mutations/companyMutations'
import { SEARCH_LABS_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'
import { v4 as uuidv4 } from 'uuid'
import { useForceUpdate } from '../../../../shared/hooks/useForceUpdate'
import CreateNewItemExtraButtons from '../../../common/CreateNewItemExtraButtons'
import MergeLabButton from './MergeLabButton'

const LabsForm = props => {
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { user } = useUser()
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, getFieldValue, setFieldsValue } = form

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  useEffect(() => {
    resetFields()
  }, [formVisible])

  const [addNewLab, { loading }] = useMutation(ADD_NEW_LAB, {
    refetchQueries: [{ query: SEARCH_LABS_QUERY, variables: { searchTerm: '' } }]
  })
  const [editLab, { loading: loadingEdit }] = useMutation(EDIT_LAB)
  const [deleteLab, { loading: loadingDelete }] = useMutation(DELETE_LAB, {
    update: handleUpdateAfterDelete
  })

  function handleUpdateAfterDelete(store, deletedItem) {
    store.data.delete(`Lab:${deletedItem.data.deleteLab.id}`)
    props.handleAnimateRow && props.handleAnimateRow(null, true)
  }

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          let lab

          if (selectedItem.new) {
            lab = await addNewLab({
              variables: {
                ...values,
                number: values.number ? `${values.suffix || ''}_${values.number}` : ''
              }
            })
            if (key === 'view') {
              navigate(`/associates/lab/${lab.data.addNewLab.id}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
            } else {
              handleClose(lab.data.addNewLab)
            }
          } else {
            lab = await editLab({
              variables: {
                ...values,
                number: values.number ? `${values.suffix || ''}_${values.number}` : '',
                lab: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)
          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (lab.data.addNewLab && lab.data.addNewLab.id) || (lab.data.editLab && lab.data.editLab.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  const handleDelete = async () => {
    try {
      await deleteLab({
        variables: {
          lab: selectedItem.id
        }
      })
      message.success(t('common.deletedSuccess'))
      handleClose()
      navigate('/associates/labs')
    } catch (e) {
      if (e?.message.includes('Delete orders first')) {
        message.error(t('app.deleteOrders'))
      } else {
        showAuthError(e, t, setLoginModalVisible)
      }
    }
  }

  return (
    <StyledDrawer
      title={selectedItem.new ? t('app.newLab') : `${t('app.editLab')} - ( ${selectedItem.name} )`}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new && user.permissions.deleteLab ? (
            <Space>
              <Popconfirm
                title={t('common.sureDelete')}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={handleDelete}
                disabled={loading || loadingEdit}
              >
                <Button loading={loadingDelete} disabled={loading || loadingEdit} type="primary" danger icon={<DeleteOutlined />}>
                  {t('common.delete')}
                </Button>
              </Popconfirm>
              <MergeLabButton
                lab={selectedItem}
                disabled={loading || loadingEdit || loadingDelete}
                afterMerge={() => {
                  handleClose()
                  navigate('/associates/labs')
                }}
              />
            </Space>
          ) : selectedItem.new && !selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : null}
          <Button
            loading={loading || loadingEdit}
            disabled={loadingDelete}
            htmlType="submit"
            type="primary"
            form={`${generatedID}LabForm`}
          >
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingDelete || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}LabForm`}
          onFinish={handleSubmit}
          initialValues={{
            ...selectedItem,
            number: selectedItem.number?.split('_')[1] || '',
            suffix: selectedItem.number?.split('_').length > 1 ? selectedItem.number.split('_')[0] : user.company.code
          }}
        >
          <Row type="flex" gutter={16}>
            <Col md={12} xs={24}>
              <NameField autoFocus required label={t('common.name')} min={0} />
            </Col>
            <Col md={12} xs={24}>
              <PhoneField
                getFieldValue={getFieldValue}
                setFieldsValue={setFieldsValue}
                label={t('calendar.companyNumber')}
                fieldName="number"
              />
            </Col>
            <Col md={12} xs={24}>
              <EmailField />
            </Col>
            <Col span={24}>
              <Form.Item label={t('common.details')} name="details" rules={[{ max: 1000, message: t('calendar.detailsLong') }]}>
                <Input.TextArea dir="auto" rows={4} maxLength={1000} showCount placeholder={t('calendar.addDetails')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="disabled" valuePropName="checked">
                <Checkbox>{t('common.disabled')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

LabsForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func
}

export default LabsForm
